import React from 'react';
import { TableCell, TableRow, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { kgToLbs, prettyDate } from '../../../lib/helpers/helpers';
import { differenceInDays } from 'date-fns';
import { getTrend, getWeightData, getNoData } from '../../PatientsScreen/components/PatientDietQualityReport';
import PatientAssessmentLinkComponent from './PatientAssessmentLinkComponent';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
  openIcon: theme.globalClasses.adminTableIcon,
  tableRow: {
    ...theme.globalClasses.adminTableRowWrapper,
    '& .MuiTypography-root.MuiTypography-body1': {
      fontSize: '14px',
    },
  },
  dietQualityLink: {
    display: 'inline-flex',
    alignItems: 'center',
  },
}));

const TrendDisplay = ({ value, isImproving, showCaret = true, useDefaultColor = false }) => {
  const icon = showCaret ? (
    isImproving ? (
      <ExpandLess
        color="success"
        sx={{ width: 18, height: 18 }}
      />
    ) : (
      <ExpandMore
        color="error"
        sx={{ width: 18, height: 18 }}
      />
    )
  ) : null;

  return (
    <Typography
      variant="body1"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        color: useDefaultColor ? 'inherit' : isImproving ? 'success.main' : 'error.main',
      }}
    >
      {value} {icon}
    </Typography>
  );
};

const getTrendDisplay = (pre, post, options = {}) => {
  const { showSign = true, showNoChangeAsDash = false, showCaret = true, higherIsBetter = true } = options;

  if (isNaN(pre) || (!pre && !post)) return getNoData();

  pre = Number(pre);
  post = Number(post) || 0;

  if (pre === post) {
    return (
      <TrendDisplay
        value={showNoChangeAsDash ? '--' : pre}
        isImproving={true}
        showCaret={false}
        useDefaultColor={true}
      />
    );
  }

  const diff = Math.abs(post - pre);
  const isImproving = higherIsBetter ? post > pre : post < pre;
  const sign = isImproving ? '+' : '-';

  return (
    <TrendDisplay
      value={showSign ? `${sign}${diff}` : pre}
      isImproving={isImproving}
      showCaret={showCaret}
    />
  );
};

const getHeiData = (current, previous) => {
  if (isNaN(current) || (!current && !previous)) return getNoData();
  if (!previous)
    return (
      <TrendDisplay
        value={current}
        isImproving={true}
        showCaret={false}
        useDefaultColor={true}
      />
    );

  const isImproving = current > previous;
  return (
    <TrendDisplay
      value={current}
      isImproving={isImproving}
    />
  );
};

const getTrendData = (pre, post) => {
  return getTrendDisplay(pre, post, { showNoChangeAsDash: true, showCaret: false, higherIsBetter: false });
};

const PatientLatestAssessmentRow = ({ row, summary, previousReport }) => {
  const classes = useStyles();

  const daysBetween = previousReport?.createdAt
    ? differenceInDays(new Date(row.createdAt), new Date(previousReport?.createdAt))
    : 'N/A';

  // trend - idealDiet is post - better than pre
  const trend = getTrendData(row?.dietQuality, row?.idealQuality, false);
  // hei - pre is current and post is previous
  const hei = getHeiData(row?.dietHEI, previousReport?.dietHEI, true);
  // bmi - we want post to be better than pre
  const bmi = getTrend(row?.preAndPost?.postBmi, row?.preAndPost?.preBmi, false);
  // weight - we don't want color change
  const currentWeight = row?.weight_lbs ? row.weight_lbs : row?.weight_kg ? kgToLbs(row.weight_kg) : 0;
  const previousWeight = previousReport?.weight_lbs
    ? previousReport.weight_lbs
    : previousReport?.weight_kg
    ? kgToLbs(previousReport.weight_kg)
    : 0;
  const weight = getWeightData(currentWeight, previousWeight);

  return (
    <TableRow
      tabIndex={-1}
      id={row.id}
      key={row.id}
      className={classes.tableRow}
    >
      <TableCell align="left">{prettyDate(row.createdAt)}</TableCell>
      <TableCell align="left">
        <PatientAssessmentLinkComponent
          dietQuality={row?.dietQuality}
          dietName={row?.dietName}
          link={row?.workflowShareUrl}
        />
      </TableCell>
      <TableCell align="left">{trend}</TableCell>
      <TableCell align="left">{row.idealQuality ? `${row.idealQuality} - ${row.idealName}` : 'N/A'}</TableCell>
      <TableCell align="left">{hei}</TableCell>
      <TableCell align="left">{bmi}</TableCell>
      <TableCell align="left">{weight}</TableCell>
      <TableCell align="left">{daysBetween}</TableCell>
    </TableRow>
  );
};

export default PatientLatestAssessmentRow;
